import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Row,
  Col,
  Button,
  Form,
  ToggleButton,
  ToggleButtonGroup,
  ButtonGroup,
} from 'react-bootstrap';
import Slider from '@material-ui/core/Slider';

import Panel from 'components/Hardware/Panel';
import { FloatSelect, FloatInput } from 'components/Form/Inputs';
import useNumberDraft from './useNumberDraft';

// racking buffers in mm, labels in inches
const bufferOptions = [
  { value: 19.05, label: '3/4"' },
  { value: 12.7, label: '1/2"' },
];

const Design = ({
  currentPanel,
  buffer,
  rowSpacing,
  panelTilt,
  azimuthRestriction,
  changePanelHardware,
  placementHandler,
  bufferHandler,
  rowSpacingHandler,
  panelTiltHandler,
  azimuthRestrictionHandler,
  solarPanels,
  panelPlacementFillMode,
  setPanelPlacementFillMode,
  panelPlacementLayoutMode,
  setPanelPlacementLayoutMode,
  panelPlacementOnlySelectedRoof,
  setPanelPlacementOnlySelectedRoof,
  clearPanels,
}) => {
  const [serverSide, setServerSide] = useState(false);

  // currentPanel is now an object (full hardware), use it directly
  const selected = currentPanel;

  const panelOptions = Array.isArray(solarPanels)
    ? solarPanels
        .filter(Boolean)
        .map((pnl) => {
          const manufacturerName =
            pnl?.manufacturer?.name ||
            pnl?.manufacturer_name ||
            pnl?.manufacturerName ||
            'Unknown';
          const model = pnl?.model || '';
          const label = model ? `${manufacturerName}-${model}` : manufacturerName;
          return { id: pnl.id, label };
        })
    : [];

  const handlePanelChoice = (e) => {
    const { value } = e.target;
    const panel =
      solarPanels.find(({ id }) => String(id) === String(value)) || null;
    if (!panel) return;
    changePanelHardware(panel);
  };

  const handleFillModeChange = (e) => {
    const fillMode = Number(e.target.value);
    setPanelPlacementFillMode(fillMode);
  };

  const handleBuffer = (e) => bufferHandler(+e.target.value);
  const handleAzimuthRestriction = (e, value) => azimuthRestrictionHandler(value);

  // Draft-aware bindings for the numeric float inputs. Default
  // skipEmptyCommit (true) keeps the parent's stored number intact while
  // the user is mid-edit — clearing the field doesn't push undefined
  // through to downstream canvas/calculation code, and blur snaps the
  // draft back to the parent value if the user didn't type anything.
  const rowSpacingInput = useNumberDraft(rowSpacing, rowSpacingHandler);
  const panelTiltInput = useNumberDraft(panelTilt, panelTiltHandler);

  return (
    <>
      {/* <Row className="mt-2">
        <Col>
          <Form.Check
            id="chk-panel-placement-server-side"
            type="switch"
            checked={serverSide}
            onChange={(e) => setServerSide(e.target.checked)}
            label="Run Server-Side"
          />
        </Col>
      </Row> */}
      <Row className="mb-2 mr-3">
        <Col sm="12">
          <FloatSelect
            label="Solar Panel"
            options={panelOptions}
            labelField="label"
            value={selected?.id || ''}
            onChange={handlePanelChoice}
          />
        </Col>
      </Row>

      <Row className="my-4 mr-3">
        <Col sm="12">
          <FloatSelect
            label="Racking Buffer"
            options={bufferOptions}
            valueField="value"
            labelField="label"
            value={buffer}
            onChange={handleBuffer}
            nullValue="None"
            allowNull
          />
        </Col>
        <Col className="mt-1" sm="12">
          <FloatInput
            label="Row Spacing"
            type="number"
            append="in"
            {...rowSpacingInput}
          />
        </Col>
        <Col className="mt-1" sm="12">
          <FloatInput
            label="Tilt"
            type="number"
            append="deg"
            {...panelTiltInput}
          />
        </Col>
        <Col className="mt-3" sm="12">
          <Form.Label style={{ fontSize: '14px', color: '#6c757d' }}>
            Azimuth Restriction: {azimuthRestriction[0]}° - {azimuthRestriction[1]}°
          </Form.Label>
          <Slider
            value={azimuthRestriction}
            onChange={handleAzimuthRestriction}
            valueLabelDisplay="auto"
            min={0}
            max={360}
            marks={[
              { value: 0, label: '0°' },
              { value: 90, label: '90°' },
              { value: 180, label: '180°' },
              { value: 270, label: '270°' },
              { value: 360, label: '360°' },
            ]}
            style={{ color: '#007bff' }}
          />
        </Col>
        <Col className="mt-4" sm="12">
          <Form.Check
            id="selected_roof"
            type="switch"
            checked={panelPlacementOnlySelectedRoof}
            onChange={(e) =>
              setPanelPlacementOnlySelectedRoof(!panelPlacementOnlySelectedRoof)
            }
            label="Only Selected Roof Segments"
          />
        </Col>
      </Row>
      <Row className="mr-3">
        <Col>
          <Form.Control
            as="select"
            value={panelPlacementFillMode}
            onChange={handleFillModeChange}
          >
            <option value={3}>Vertical First</option>
            <option value={4}>Horizontal First</option>
            <option value={2}>Vertical</option>
            <option value={1}>Horizontal</option>
          </Form.Control>
        </Col>
      </Row>
      <Row className="mt-1 mr-3">
        <Col>
          <ToggleButtonGroup type="checkbox" style={{ width: "100%" }}>
            <ToggleButton value={1} style={{ color: panelPlacementLayoutMode === 1 ? 'white' : 'gray' }} onClick={() => setPanelPlacementLayoutMode(1)}>Auto</ToggleButton>
            <ToggleButton value={2} style={{ color: panelPlacementLayoutMode === 2 ? 'white' : 'gray' }} onClick={() => setPanelPlacementLayoutMode(2)}>Grid-Mode</ToggleButton>
            <ToggleButton value={3} style={{ color: panelPlacementLayoutMode === 3 ? 'white' : 'gray' }} onClick={() => setPanelPlacementLayoutMode(3)}>Brick-Mode</ToggleButton>
            <ToggleButton value={4} style={{ color: panelPlacementLayoutMode === 4 ? 'white' : 'gray' }} onClick={() => setPanelPlacementLayoutMode(4)}>Compact</ToggleButton>
          </ToggleButtonGroup>
        </Col>
      </Row>
      <Row className="mt-1 mr-3">
        <Col>
          <ButtonGroup style={{ width: '100%' }}>
            <Button
              onClick={(e) => placementHandler(e, serverSide, panelPlacementFillMode, panelPlacementLayoutMode)}
              disabled={!selected}
            >
              Fill Panels
            </Button>
            <Button onClick={(e) => clearPanels()} disabled={!selected}>
              Clear
            </Button>
          </ButtonGroup>
        </Col>
      </Row>
      {selected && (
        <Row className="my-3 mx-1 border">
          <Panel panel={selected} />
        </Row>
      )}
    </>
  );
};

Design.propTypes = {
  currentPanel: PropTypes.object, // Full panel hardware object
  buffer: PropTypes.number.isRequired,
  rowSpacing: PropTypes.number,
  panelTilt: PropTypes.number,
  azimuthRestriction: PropTypes.arrayOf(PropTypes.number),
  changePanelHardware: PropTypes.func.isRequired,
  placementHandler: PropTypes.func.isRequired,
  bufferHandler: PropTypes.func.isRequired,
  rowSpacingHandler: PropTypes.func,
  panelTiltHandler: PropTypes.func,
  azimuthRestrictionHandler: PropTypes.func,
  solarPanels: PropTypes.arrayOf(PropTypes.object).isRequired,
  panelPlacementFillMode: PropTypes.number.isRequired,
  setPanelPlacementFillMode: PropTypes.func.isRequired,
  panelPlacementLayoutMode: PropTypes.number.isRequired,
  setPanelPlacementLayoutMode: PropTypes.func.isRequired,
  panelPlacementOnlySelectedRoof: PropTypes.bool.isRequired,
  setPanelPlacementOnlySelectedRoof: PropTypes.func.isRequired,
  clearPanels: PropTypes.func.isRequired,
};

export default Design;
